import { MODELS, useMultiModel } from "../../../api";
import shuffleArray from "../../../lib/shuffle.js";
import { Roles } from "../../../lib/Roles.jsx";
import { ROUTES } from "../../../routes.js";
import { useSparkContext } from "../context/SparkContext.jsx";
import { SparkShowroomOverviewWithData } from "./SparkShowroomOverviewWithData.jsx";

/**
 * Represents the overview of a showroom for a Spark in a workshop.
 * This component fetches and displays showroom entries, shared entries, unseen comments, and workshop settings.
 */
const SparkShowroomOverview = () => {
  // workshop / spark / settings come from context and are always present here.
  const { workshop, spark, settings } = useSparkContext();
  const sparkSlug = spark.slug;

  const isStudent = workshop.roles?.includes(Roles.STUDENT);

  // Fire the independent fetches in parallel (stable-length array; null keeps the
  // student-only smallTeam slot). All-or-nothing: suspends until every entry resolves.
  const [showroomEntries, unseenComments, sharedEntries, smallTeam] =
    useMultiModel([
      MODELS.WORKSHOP_SHOWROOM_ENTRIES(workshop.id),
      MODELS.COMMENTS_UNSEEN({ workshopId: workshop.id }),
      MODELS.SPARK_SHARED_SHOWROOM_ENTRIES(spark.id),
      isStudent ? MODELS.SMALL_TEAM({ workshopId: workshop.id }) : null,
    ]);

  // Dependent on smallTeam (which may be absent), so it's a conditional fetch in
  // its own slot rather than joining the parallel batch above.
  const [smallTeamShowroomData] = useMultiModel([
    smallTeam ? MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeam.id) : null,
  ]);

  if (settings.projectSortMode === "random") {
    shuffleArray(showroomEntries);
  }

  const currentEntryIds = new Set(showroomEntries?.map((e) => e.id));
  const otherSharedEntries = sharedEntries?.filter(
    (e) => !currentEntryIds.has(e.id),
  );

  const unseenByEntryId = unseenComments.reduce((m, uc) => {
    m[uc.showroomEntryId] = (m[uc.showroomEntryId] || 0) + 1;
    return m;
  }, {});

  const baseUrl = ROUTES.spark.getUrl({
    workshopSlug: workshop.slug,
    sparkSlug,
  });
  const hasExistingShowroomEntry = smallTeamShowroomData?.entry;
  const uploadEditButtonTarget = smallTeam
    ? `${baseUrl}/team-diary/${smallTeam.id}`
    : null;

  const publicShowroomButtonTarget = workshop?.publicShowroom
    ? `${import.meta.env.VITE_SHOW_HOST}/room/${workshop.publicShowroom.slug}`
    : null;

  return (
    <SparkShowroomOverviewWithData
      entries={showroomEntries}
      otherSharedEntries={otherSharedEntries}
      unseenByEntryId={unseenByEntryId}
      workshopSettings={settings}
      hasExistingShowroomEntry={hasExistingShowroomEntry}
      publicShowroomButtonTarget={publicShowroomButtonTarget}
      uploadEditButtonTarget={uploadEditButtonTarget}
    />
  );
};

export default SparkShowroomOverview;
